/**
 * useDemoMode
 *
 * Single source of truth for "is the user currently looking at demo data".
 *
 * Demo mode is ACTIVE when:
 *   - the org has been seeded with demo records (`hasDemoData` from /status), AND
 *   - the org has no real (non-demo) records yet — an ORGANIZATION fact
 *     (`hasRealData` from /status), not what the viewer's scope happens to show.
 *
 * Once the user imports/uploads their first real record, demo mode flips off
 * automatically — even though the demo rows themselves stay in D1 until the
 * user explicitly clears them or until the auto-clear threshold is hit.
 *
 * Task 18 ships only the hook; wiring it into queries/UI is Tasks 19-22.
 */

import { useIsFetching, useQuery, useQueryClient } from '@tanstack/react-query';
import { useApiClient } from '@/hooks/useApiClient';
import { isAccessRefetching } from '@/hooks/useAccessMe';
import { useOrganization } from '@/contexts/RoleContext';
import { useCanOpenPath, useScreenSubject } from '@/contexts/ScreenSubjectContext';
import type { ApiClient } from '@/lib/api';
import { retryServerFaultOnce } from '@/lib/query-retry';

export interface DemoModeState {
  /**
   * The status probe is loading — or, for a status without `hasRealData` (old
   * edge-api), the real-records probe is.
   */
  isLoading: boolean;
  /** True iff demo rows exist AND the org has no real rows (org-wide, any viewer). */
  isDemoActive: boolean;
  /** True iff demo rows exist (independent of real rows). */
  hasDemoData: boolean;
  /**
   * Count of real (non-demo) records the VIEWER can see (role-scoped list).
   * Display only: never derive demo policy from it — an agent without their
   * own records in an org that has them would read 0.
   */
  realRecordsCount: number;
  /** ID of the "aha" demo call to spotlight (null until seed completes). */
  ahaCallId: string | null;
}

const STATUS_STALE_MS = 60_000;
const REAL_COUNT_STALE_MS = 30_000;

/**
 * Core query logic, parameterized on apiClient/orgId instead of pulling them
 * from the Clerk-only `useApiClient()`/`useOrganization()` hooks.
 *
 * Use this variant from components that render in BOTH the Clerk app and the
 * embedded/Bitrix24 iframe (e.g. AdminDashboard, ClientDashboard,
 * SupervisorDashboard) and already have apiClient/orgId from the mode-agnostic
 * `useAuth()` (UnifiedAuthContext). Calling `useOrganization()` from those
 * components would throw — the embedded tree provides its own, differently
 * scoped `OrganizationContext` (see EmbeddedRoleProvider), not the one backing
 * `@/contexts/RoleContext`.
 */
export function useDemoModeFromAuth(
  apiClient: ApiClient,
  orgId: string | null
): DemoModeState {
  const statusQ = useQuery({
    queryKey: ['demo', 'status', orgId],
    queryFn: () => apiClient.demo.getStatus(),
    enabled: !!orgId,
    staleTime: STATUS_STALE_MS,
    // `fetchWithRetry` already repeats a network failure or a 503 for a GET,
    // and a second layer over those would stack the attempts (see
    // lib/query-retry.ts). It does not repeat a 500/502/504, and one of them
    // would end as `includeDemo: 'no'` — empty dashboards, Voice of Customer
    // and deal queue for an org with only demo data (#1827): this layer asks
    // those once. A failure that survives is treated as persistent — the safe
    // guess: demo rows are never mixed into what may be real data (#1812).
    retry: retryServerFaultOnce,
  });

  // The count reads the records list. A grant holder without `records.read`
  // would get a 403 from it on every dashboard load: no request (#1703, #1336).
  // Its answer would prove nothing anyway (see `waitsForRealCount`); without a
  // grant the list is always asked, as before.
  //
  // While `/access/me` is still loading `canOpenPath` answers «open» (the role
  // decides until the grant is known), so the count would fire on a cold load
  // and still 403 for the grant holder: it waits for the answer (Codex #1715).
  const screenSubject = useScreenSubject();
  const accessSettled =
    !screenSubject || screenSubject.isOwner || screenSubject.access.status !== 'loading';
  const recordsReadable = useCanOpenPath()('/records');
  const queryClient = useQueryClient();
  // `/access/me` asked again — after a growth of the access revision, on focus:
  // its previous answer still says «open» meanwhile, so the count waits for
  // the new one instead of asking a list it may now be refused (E1.5 PR 4b).
  const accessRefetching = useIsFetching({ queryKey: ['access-me', orgId] }) > 0;
  // Focus and reconnect refetch the count only while `/access/me` is settled
  // AND not being asked again. It refetches on those events too and keeps its
  // previous answer while it does, so a subject narrowed while the tab was
  // away would still look «open» and ask the list it is now refused (Codex P2,
  // #1715). Evaluated when the event fires, against the live query cache: a
  // render-time flag would still read «settled» at that instant.
  const refetchWhenAccessSettled = () => !isAccessRefetching(queryClient, orgId);
  const realCountQ = useQuery({
    queryKey: ['records', 'count', { includeDemo: 'no' }, orgId],
    queryFn: () => apiClient.records.getRecords({ includeDemo: 'no', limit: '1' }),
    enabled: !!orgId && accessSettled && recordsReadable && !accessRefetching,
    staleTime: REAL_COUNT_STALE_MS,
    refetchOnWindowFocus: refetchWhenAccessSettled,
    refetchOnReconnect: refetchWhenAccessSettled,
  });

  const hasDemoData = statusQ.data?.hasDemoData ?? false;
  const realRecordsCount = realCountQ.data?.total ?? 0;
  const hasRealDataFlag = statusQ.data?.hasRealData;
  // Any evidence of real data turns demo off. The org-wide flag is the primary
  // signal, but a `false` from /status can be behind the viewer's own list (a
  // 60 s cached status right after the first real upload; rows with a NULL
  // organization_id the status SQL cannot see), so a positive viewer count wins
  // over it. The reverse never holds: a viewer count of 0 proves nothing (an
  // agent without own records in an org that has them reads 0), so it can't
  // turn demo back on over `hasRealData: true`.
  const orgHasRealData = hasRealDataFlag === true || realRecordsCount > 0;
  // Only a confirmed `hasRealData: true` lets the decision skip the viewer
  // count. A `false` (possibly a 60 s cached /status) or a missing field must
  // wait for it: deciding "demo" early lets useFirstLoginRedirect navigate to
  // the demo call, and a later positive count cannot undo that navigation
  // (local Codex P2, #1356).
  // A disabled query reports `isLoading: false`, so the pending access answer
  // is a wait of its own: whether the count is asked at all is not known yet.
  const waitsForRealCount =
    hasRealDataFlag !== true && (realCountQ.isLoading || (!!orgId && !accessSettled));

  return {
    isLoading: statusQ.isLoading || waitsForRealCount,
    isDemoActive: hasDemoData && !orgHasRealData,
    hasDemoData,
    realRecordsCount,
    ahaCallId: statusQ.data?.ahaCallId ?? null,
  };
}

export function useDemoMode(): DemoModeState {
  const { apiClient } = useApiClient();
  const { selectedOrgId } = useOrganization();
  return useDemoModeFromAuth(apiClient, selectedOrgId);
}
